/** @file gizmoLine: one camera-facing handle line (GreasedLine); colour, width and alpha change in place. */
import { GreasedLineMeshMaterialType } from '@babylonjs/core/Materials/GreasedLine/greasedLineMaterialInterfaces.js';
import type { Material } from '@babylonjs/core/Materials/material.js';
import type { Color3 } from '@babylonjs/core/Maths/math.color.js';
import type { Vector2 } from '@babylonjs/core/Maths/math.vector.js';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import { CreateGreasedLine } from '@babylonjs/core/Meshes/Builders/greasedLineBuilder.js';
import type { GreasedLineBaseMesh } from '@babylonjs/core/Meshes/GreasedLine/greasedLineBaseMesh.js';
import type { Scene } from '@babylonjs/core/scene.js';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { VIEWPORT_GROUPS } from './viewportGroups';
import { VIEWPORT_LAYERS } from './viewportLayers';

/**
 * The line mesh; `style(color, width, alpha)` sets its colour (a shared, never-mutated Color3), width in scene units
 * (CSS px x scene units per CSS px: the overlay camera is orthographic) and alpha, touching the material only on a
 * change; `resize` gives the material the render size (its aspect keeps the width even in every direction).
 */
export type GizmoLine = { mesh: GreasedLineBaseMesh; style(color: Color3, width: number, alpha?: number): void; resize(size: Vector2): void };

/**
 * A GreasedLine through `points` (already in `parent`'s local coordinates) on an unlit StandardMaterial with the
 * GreasedLine plugin (its colour replaces the material's, its alpha blends), camera facing, no size attenuation, in
 * the gizmo group of the overlay layer (drawn at full resolution with MSAA), never picked, never culled.
 */
export function gizmoLine(name: string, points: readonly Vec3[], parent: TransformNode, scene: Scene, color: Color3): GizmoLine {
  const flat = points.flatMap((p) => [p[0], p[1], p[2]]);
  const mesh = CreateGreasedLine(name, { points: flat }, { materialType: GreasedLineMeshMaterialType.MATERIAL_TYPE_STANDARD, sizeAttenuation: false, width: 0.1, color }, scene);
  Object.assign(mesh, { parent, renderingGroupId: VIEWPORT_GROUPS.gizmo, layerMask: VIEWPORT_LAYERS.overlay, isPickable: false, alwaysSelectAsActiveMesh: true });
  const material = mesh.material as Material & { disableLighting: boolean };
  material.disableLighting = true;
  const plugin = mesh.greasedLineMaterial;
  let shown = { color, width: 0.1, alpha: 1 };
  return {
    mesh,
    style: (c, w, a = 1) => {
      if (c === shown.color && w === shown.width && a === shown.alpha) return;
      if (plugin !== undefined) { plugin.color = c; plugin.width = w; }
      material.alpha = a;
      shown = { color: c, width: w, alpha: a };
    },
    resize: (size) => { if (plugin !== undefined) plugin.resolution = size; },
  };
}
